<template>
  <div class="application form">
    <div class="application__title">
      <h2>
        Application Form
      </h2>
      <div
        v-if="step.status && step.status === 'In Review'"
        class="application__status"
      >
        <span>
          {{step.status}}
        </span>
        <span
          class="step-info"
          :id="'step-info'+step.id"
        >!</span>
        <b-tooltip 
          :target="'step-info'+step.id"
          triggers="hover"
          custom-class="step-info__tooltip"
        >
          This step is in review. You will receive the notification when it is done.
        </b-tooltip>
      </div>
    </div>
    <h2 class="application__program">Program: <span>{{ programName }}</span></h2>
    <h2 class="application__program capitalize">{{ camperStudent('singular') }}: <span>{{ userName }}</span></h2>

    <AlertMessage
      v-if="step.info_message"
      :message="step.info_message"
    />

    <ApplicationPaymentContract
      v-if="step.hasPC && step.display_pc_on_top"
      :hide-price-breakdown="step.hide_price_breakdown"
      :step-status="step.status"
      @viewableCC="viewableCC"
      @viewableACH="viewableACH"
    />

    <ApplicationAddRow
      v-for="field in formFields"
      v-show="!field.hidden"
      :key="field.id"
      :fields-data="field"
      :field-value="field.value"
      :field-options="field.options"
      :step-status="step.status"
      :step-id="step.id"
      :valid-tracker="validTracker"
      :check-value="field.hidden"
      :is-disabled-editing="!step.isEditable"
      :is-signature-invalid="signatureInvalidRefs.includes(field.ref)"
      :payment-fees="paymentFees"
      :payment-method="feePaymentMethod"
      ref="rows"
      @clear-invalid="onClearSignatureInvalid"
      @recalcTotal="recalcTotal"
    />

    <ApplicationPaymentContract
      v-if="step.hasPC && !step.display_pc_on_top"
      :hide-price-breakdown="step.hide_price_breakdown"
      :step-status="step.status"
      @viewableCC="viewableCC"
      @viewableACH="viewableACH"
    />

    <div v-if="hasAnyPaymentFields && !isDisabledCCOnStepWithPayments">
      <div class="payment-wizard-total">
        <span>Total</span>
        <span>($){{ totalSum.toFixed(2) }}</span>
      </div>
    </div>

    <CreditCard
      v-if="step.isEditable && (hasAnyPaymentFields || step.hasPC) && !isDisabledCCOnPcStep && !isDisabledCCOnStepWithPayments"
      @authCreditCard="pay"
      :hideCC="isHideCCBlock"
      :hide-a-c-h="isHideACHBlock"
      :btnText="isLastStep ? programCompleteRegistrationBtnText : 'Pay'"
      :is-btn-disabled="(paymentContract && paymentContract.optionInvalid) || step.forceDisablingSendButton"
      :disabled-button-info-message="step.disabledButtonInfoMessage"
      :force-disabling-send-button="step.forceDisablingSendButton"
    />

    <p class="form__error">{{ errorMessage }}</p>

    <div
      v-if="validationErrors.length"
      class="form-group"
    >
      <p
        v-for="(validationError, index) in validationErrors"
        :key="index"
        class="text-center form__error"
      >
        {{ validationError }}
      </p>
    </div>

    <div
      v-if="showButtons && step.disabledButtonInfoMessage && step.forceDisablingSendButton"
      class="form__disabled_error_message"
    >
      {{ step.disabledButtonInfoMessage }}
    </div>

    <div
      v-if="showButtons || !step.isEditable"
      class="form__one-row"
    >
      <button
        v-if="tabIndex === 0"
        class="btn btn--color-outline-inactive form__button input-button_left"
        :disabled="isLoading"
      >{{ previousButtonText }}</button>
      <button
        v-if="tabIndex > 0"
        class="btn btn--color-outline-dark form__button input-button_left"
        :disabled="isLoading"
        @click="$store.commit('applicationRegistration/DECREMENT_TAB_INDEX')"
      >{{ previousButtonText }}</button>
      <button
        v-if="(isStepWaitingForApprove || isLastStep) && !step.isEditable"
        :disabled="true"
        class="btn btn--color-dark form__button"
      >
        <span>{{ sendButtonText }}</span>
      </button>
      <button
        v-else-if="!step.isEditable"
        class="btn btn--color-dark form__button"
        :disabled="isLoading"
        @click="$store.commit('applicationRegistration/INCREMENT_TAB_INDEX')"
      >
        {{ sendButtonText }}
      </button>
      <button
        v-else
        :disabled="step.isSendButtonDisabled || step.forceDisablingSendButton || isLoading"
        class="btn btn--color-dark form__button"
        @click="sendStepForm()"
      >
        <span>{{ sendButtonText }}</span>
      </button>
    </div>

    <div v-if="showButtons && step.isEditable && !isLastStepWaitingForApprove && !isStepWaitingForApprove">
      <button
        v-if="step.status !== 'Approved'"
        :disabled="isSaveButtonDisabled || isLoading"
        class="btn btn--color-outline-dark form__button btn-continue"
        @click="sendStepForm(true)"
      >{{ saveContinueLaterButtonText }}
      </button>
    </div>


    <div
      v-if="isAdditionalButtonsWithRedirectAvailable"
      class="form__one-row"
    >
      <button
        class="btn btn--color-outline-bright form__button input-button_left"
        @click="registerAnotherChild"
        :disabled="isLoading"
      >{{ registerAnotherChildButtonText }}</button>

      <button
        class="btn btn--color-outline-bright form__button"
        @click="registerForAnotherProgram"
        :disabled="isLoading"
      >{{ registerAnotherProgramButtonText }}</button>
    </div>
  </div>

</template>

<script>
  import { BTooltip } from 'bootstrap-vue'
  import ApplicationAddRow from '@/views/Application/ApplicationAddRow.vue'
  import ApplicationPaymentContract from '@/views/Application/ApplicationPaymentContract.vue'
  import CreditCard from '@/components/site/CreditCard.vue'
  import AlertMessage from '@/components/site/AlertMessage.vue'
  import { checkConditionValues } from '@core/mixins/checkConditionValues'
  import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'
  import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
  import { mapGetters } from 'vuex'
  import store from '@/store'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
  import { preparePaymentOptionPayload } from '@/utils/preparePaymentOptionPayload'
  import { calculateApplicableFees } from '@/utils/payment'
  import moment from 'moment'

  export default {
    components: {
      BTooltip,

      ApplicationAddRow,
      ApplicationPaymentContract,
      CreditCard,
      AlertMessage,
    },
    props: {
      step: {
        type: Object,
        require: true,
      },
      formFields: {
        type: Array,
        default: () => []
      },
    },
    mixins: [checkConditionValues, instanceSessionCamperNames, prepareProgramRegistrationInfo],
    data() {
      return {
        programId: this.$router.currentRoute.params.id,
        validTracker: false,
        errorMessage: '',
        validationErrors: [],
        isSaveButtonDisabled: false,
        isSendButtonDisabled: false,
        hideCCForPC: false,
        hideACHForPC: false,
        refreshKey: 0,
        signatureInvalidRefs: [],
      }
    },
    watch: {},
    async created() {
      let stepHasPayments = false
      try {
        stepHasPayments = this.hasAnyPaymentFields > 0
      } catch (_) {
        stepHasPayments = false
      }

      if (stepHasPayments) {
        await this.loadCards()
      }
    },
    computed: {
      ...mapGetters({
        applicationId: 'applicationRegistration/getApplicationId',
        editingStateVersion: 'applicationRegistration/getEditingStateVersion',
        userId: 'applicationRegistration/getUserId',
        programName: 'applicationRegistration/getProgramName',
        programCompleteRegistrationBtnText: 'applicationRegistration/getProgramCompleteRegistrationBtnText',
        paymentContract: 'applicationRegistration/getPaymentContract',
        tabIndex: "applicationRegistration/getCurrentTabIndex",
        stepsList: 'applicationRegistration/getStepsList',
        hiddenStepsIds: 'applicationRegistration/getHiddenStepsIds',
        allConditions: 'applicationRegistration/getAllConditions',
        lastStep: 'applicationRegistration/getLastStep',
        paymentContractIsExist: 'applicationRegistration/getPaymentContractIsExist',
        userName: 'applicationRegistration/getUserName',
        productList: 'applicationRegistration/getProductsList',
        paymentFees: 'applicationRegistration/getPaymentFees',
        selectedCardInfo: 'cards/getSelectedCardInfo',
        authSettings: 'authSettings/getAuthSettings',
      }),
      isLoading: {
        get() {
          return store.getters['applicationRegistration/getIsLoading']
        },
        set(value) {
          this.$store.commit('applicationRegistration/SET_IS_LOADING', value)
        },
      },
      isLastStep() {
        return this.lastStep.id == this.step.id
      },
      isStepWaitingForApprove() {
        return this.step.requirement_option === 'Requires Approval To Continue' && this.step.status !== 'Approved'
      },
      isLastStepWaitingForApprove() {
        if (this.isLastStep && (this.step.requirement_option === 'Requires Approval' && !['Approved', 'Rejected'].includes(this.step.status))) {
          return true
        }
        if (this.isLastStep && (this.step.requirement_option === 'Requires Approval To Continue' && !['Approved', 'Rejected'].includes(this.step.status))) {
          return true
        }
        return false
      },
      isAdditionalButtonsWithRedirectAvailable() {
        if (this.isLastStep && this.step.status === 'Approved') {
          return true
        }
        if (this.isLastStepWaitingForApprove) {
          return true
        }
        if (this.step.requirement_option === 'Requires Approval To Continue' && ['Draft', 'Rejected', 'In Review'].includes(this.step.status)) {
          return true
        }

        return this.isLastStep && this.isSendButtonDisabled
      },
      isDisabledCCOnPcStep() {
        return ['Approved', 'In Review'].includes(this.step.status)
          && this.step.hasPC
      },
      isDisabledCCOnStepWithPayments() {
        return ['Approved', 'In Review'].includes(this.step.status)
          && this.hasAnyPaymentFields > 0
      },
      showButtons() {
        return (!this.hasAnyPaymentFields || this.isDisabledCCOnStepWithPayments) && !this.step.has_payment_contract
      },
      isHideCCBlock() {
        return this.hideCCForPC && this.hasAnyPaymentFields === 0
      },
      isHideACHBlock() {
        return this.hasAnyPaymentFields > 0 || (this.step.hasPC && this.hideACHForPC)
      },
      sendButtonText() {
        if (this.isLastStep) return this.programCompleteRegistrationBtnText
        if (['Approved', 'In Review'].includes(this.step.status) || !this.step.isEditable) {
          return this.getCustomText(this.authSettings, 'application_next_button', 'Next')
        }
        if (['Requires Approval', 'Requires Approval To Continue'].includes(this.step.requirement_option)) {
          return this.getCustomText(this.authSettings, 'application_send_button', 'Send')
        }
        return this.getCustomText(this.authSettings, 'application_continue_button', 'Continue')
      },
      previousButtonText() {
        return this.getCustomText(this.authSettings, 'application_previous_button', 'Previous')
      },
      saveContinueLaterButtonText() {
        return this.getCustomText(this.authSettings, 'application_save_continue_later_button', 'Save & Continue Later')
      },
      registerAnotherChildButtonText() {
        return this.getCustomText(this.authSettings, 'application_register_another_child_button', 'Register Another Child to this Program')
      },
      registerAnotherProgramButtonText() {
        return this.getCustomText(this.authSettings, 'application_register_another_program_button', 'Register for Another Program')
      },
      currentStep: {
        get() {
          return store.getters['applicationRegistration/getStepById'](this.step.id)
        }
      },
      totalSum() {
        this.refreshKey
        const chargesSum = this.allPaymentFields.reduce((acc, cur) => {
          if (cur.type === 'product_field') {
            return acc + Number(cur ? cur.amount * cur.quantity : 0)
          } else {
            return acc + Number(cur ? cur.charge_amount : 0)
          }
        }, 0)
        return chargesSum + this.totalStepFeeAmount
      },
      /**
       * Resolve the current payment method from the selected card.
       * Falls back to the CC/ACH visibility context (isHideCCBlock / isHideACHBlock)
       * when no card has been selected yet (e.g. user is adding a new card).
       * Returns null only when we truly cannot determine the method.
       */
      feePaymentMethod() {
        // 1. Selected existing card is the most reliable source
        if (this.selectedCardInfo) {
          return this.selectedCardInfo.type?.toLowerCase() === 'ach' ? 'ACH' : 'Credit Card'
        }
        // 2. If only ACH is shown (CC hidden), infer ACH
        if (this.isHideCCBlock && !this.isHideACHBlock) {
          return 'ACH'
        }
        // 3. If only CC is shown (ACH hidden), infer Credit Card
        if (!this.isHideCCBlock && this.isHideACHBlock) {
          return 'Credit Card'
        }
        // 4. Both or neither visible — cannot determine yet
        return null
      },
      /**
       * Active charge-type contexts for this step.
       * 'PaymentWizardField' is added when there are payment_field fields.
       * 'Product' is added when there are product_field fields.
       */
      activeStepChargeTypeContexts() {
        const contexts = []
        if (this.paymentWizardFields.length > 0) contexts.push('PaymentWizardField')
        if (this.productsFields.length > 0) contexts.push('Product')
        return contexts
      },
      /**
       * Fees that apply to this step's charges given the selected payment method.
       */
      applicableStepFees() {
        if (!this.hasAnyPaymentFields || !this.feePaymentMethod) return []
        const baseAmount = this.allPaymentFields.reduce((acc, cur) => {
          if (cur.type === 'product_field') {
            return acc + Number(cur ? cur.amount * cur.quantity : 0)
          }
          return acc + Number(cur ? cur.charge_amount : 0)
        }, 0)
        return calculateApplicableFees(this.paymentFees, this.feePaymentMethod, baseAmount, this.activeStepChargeTypeContexts)
      },
      /** Sum of all applicable fee amounts */
      totalStepFeeAmount() {
        return this.applicableStepFees.reduce((sum, f) => sum + f.calculatedAmount, 0)
      },
      paymentWizardFields() {
        return (this.currentStep?.preparedFields || []).filter(field =>
          ['payment_field'].includes(field.type)
          && !field.hidden
        )
      },
      productsFields() {
        return (this.currentStep?.preparedFields || []).filter(field =>
          ['product_field'].includes(field.type)
          && !field.hidden
        )
      },
      allPaymentFields() {
        return (this.currentStep?.preparedFields || []).filter(field => {
          if (!['payment_field', 'product_field'].includes(field.type)) return false
          if (field.hidden) return false

          // additional logic for product_field
          if (field.type === 'product_field') {
            const product = this.productList.find(p => p.uuid === field.product_uuid)
            if (!product) return true
            return !product.is_in_pc
          }

          return true
        })
      },
      hasAnyPaymentFields() {
        return this.allPaymentFields.length
      },
    },
    methods: {
      async loadCards() {
        await store.dispatch('cards/loadCards')
      },
      recalcTotal() {
        this.$forceUpdate()
        this.refreshKey++
      },
      viewableCC(val) {
        this.hideCCForPC = val
      },
      viewableACH(val) {
        this.hideACHForPC = val
      },
      uploadFile(file, type = 'docs') {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', type)

        return store.dispatch('files/uploadFiles', {
          name: `application-files-${new Date().getTime()}`,
          file: formData,
        })
      },
      onClearSignatureInvalid(ref) {
        this.signatureInvalidRefs = this.signatureInvalidRefs.filter(
          r => r !== ref
        )
      },
      async validStep() {
        this.validTracker = false
        this.errorMessage = ''
        this.signatureInvalidRefs = []

        let hasError = false
        let sessionError = false

        const visibleRequiredFields = this.formFields.filter(
          field => field.required && !field.hidden
        )

        /**
         * General fields required validation
         */
        const hasEmptyRequiredField = visibleRequiredFields.some(field =>
          !['plain_text', 'signature'].includes(field.type) &&
          (field.value == null || field.value.length === 0)
        )

        if (hasEmptyRequiredField) {
          hasError = true
        }

        /**
         * Groups validation
         */
        const hasEmptyGroupSelection = visibleRequiredFields.some(field =>
          field.type === 'group_selection' &&
          (field.value == null || field.value.length === 0)
        )

        if (hasEmptyGroupSelection) {
          hasError = true
        }

        /**
         * Session validation
         */
        const hasEmptySessionSelection = visibleRequiredFields.some(field =>
          field.type === 'session_selection' &&
          (field.value == null || field.value.length === 0)
        )

        if (hasEmptySessionSelection) {
          hasError = true
          sessionError = true
        }

        /**
         * Signature validation
         */
        const rows = this.$refs.rows || []
        for (const row of rows) {
          const field = row.fieldsData

          if (field.type === 'signature' && field.required && !field.hidden) {
            if (!row.isSignatureValid()) {
              this.signatureInvalidRefs.push(field.ref)
              hasError = true
            }
          }
        }

        /**
         * Final result
         */
        if (hasError) {
          this.errorMessage = sessionError
            ? `Please, select ${this.sessionSemester}`
            : 'Please, fill required fields'

          this.validTracker = true
          return
        }
      },
      getPaymentContractData() {
        this.errorMessage = '';
        const option = this.paymentContract.options.find(option => option.id === this.paymentContract.selectedOption)

        if (!option) {
          this.errorMessage = "Select PC option"
          return
        }

        const isCustomSchedule = option.schedule_option?.allowCustomSchedule === true

        if (isCustomSchedule) {
          // Validate every row has a date and a positive amount
          const schedule = option.customSchedule ?? []
          const min = option.schedule_option.paymentAmountRange?.from ?? 1
          if (!schedule.length || schedule.length < min) {
            this.errorMessage = 'Please fill in the custom payment schedule'
            return
          }
          const endDateStr = option.schedule_option?.endDate
            ? moment(option.schedule_option.endDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : null
          for (let i = 0; i < schedule.length; i++) {
            if (!schedule[i].date) {
              this.errorMessage = `Payment #${i + 1}: date is required`
              return
            }
            if ((parseFloat(schedule[i].amount) || 0) <= 0) {
              this.errorMessage = `Payment #${i + 1}: amount must be greater than 0`
              return
            }
            if (endDateStr && schedule[i].date > endDateStr) {
              this.errorMessage = `Payment #${i + 1}: date must be on or before ${option.schedule_option.endDate}`
              return
            }
          }
        } else if (option.schedule_option.timeSchedule === 'Split over') {
          if (!option.startDate) {
            this.errorMessage = 'Select PC option Start Date'
            return
          }
          const endDate = option.schedule_option?.endDate
          if (endDate) {
            const lastPayment = moment(option.startDate, 'MM/DD/YYYY').add(option.amountOfPayments - 1, 'M')
            if (lastPayment > moment(endDate, 'MM/DD/YYYY')) {
              this.errorMessage = `Payments must be complete by ${endDate}. Please adjust the start date and amount of payments.`
              return
            }
          }
        }

        return preparePaymentOptionPayload(
          this.applicationId,
          this.paymentContract.selectedOption,
          option,
        )
      },
      pay(paymentMethodData) {
        const paymentPlanData = {
          charges: this.paymentWizardFields,
          products: this.productsFields,
        }
        const selectedCard = store.getters['cards/getSelectedCreditCard']

        // --- Selected Payment Method ---
        if (selectedCard) {
          paymentPlanData.selected_card = selectedCard

          if (paymentMethodData) {
            paymentPlanData.billing_address = paymentMethodData
          }
        } else if (paymentMethodData) {
          // Pay by card
          if (!this.isHideCCBlock) {
            paymentPlanData.card = paymentMethodData
          }

          // Pay by ACH
          if (!this.isHideACHBlock) {
            paymentPlanData.ach = paymentMethodData
          }
        }

        // --- Payment Contract ---
        if (this.step.hasPC && this.paymentContract) {
          const paymentContractData = this.getPaymentContractData()
          if (!paymentContractData) return

          paymentPlanData.paymentContractData = paymentContractData
        }

        this.sendStepForm(false, paymentPlanData)
      },
      prepareHiddenSteps(isDraft) {
        let hiddenSteps = []
        this.hiddenStepsIds.map(row => {
          if (!row.hiddenFromStepId) {
            row.hiddenFromStepId = this.step.id
          }
          if (isDraft) {
            if (row.hiddenFromStepId !== this.step.id) {
              hiddenSteps.push(row.stepId)
            }
          } else {
            let stepValue = this.stepsList.find(stepResult => stepResult.id === row.hiddenFromStepId)

            if (stepValue && stepValue.step_id === this.step.id) {
              hiddenSteps.push(row.stepId)
            }

            if (stepValue && stepValue.step_status !== 'Draft') {
              hiddenSteps.push(row.stepId)
            }
          }
        })
        return hiddenSteps
      },
      sendStepForm(isDraft = false, paymentPlanData = {}) {
        this.validTracker = false

        if (!isDraft) {
          this.validStep()
        }
        if (this.validTracker) return

        if (isDraft) {
          this.isSaveButtonDisabled = true
        } else {
          this.isSendButtonDisabled = true
        }

        store.commit('SET_LOADING_STATUS', true)
        this.isLoading = true

        let fileFieldsCount = 0
        let avatarFieldsCount = 0

        let queryParams = {
          user_id: this.userId,
          program_id: this.$router.currentRoute.params.id,
          step_id: this.step.id,
          fields: [],
          form_data: {},
          is_draft: isDraft,
          form_id: this.step.registration_form_id,
          hidden_steps: this.prepareHiddenSteps(isDraft)
        }

        if (this.applicationId) {
          queryParams.application_id = this.applicationId
          queryParams.editing_state_version = this.editingStateVersion
        }
        if (paymentPlanData.charges) {
          queryParams.charges = paymentPlanData.charges
        }

        //set products
        if (this.hasAnyPaymentFields && paymentPlanData.products) {
          queryParams.products = paymentPlanData.products
        } else if (!this.hasAnyPaymentFields && this.productsFields) {
          queryParams.products = this.productsFields
        }

        if (paymentPlanData.card) {
          queryParams.card = paymentPlanData.card
        }
        if (paymentPlanData.ach) {
          queryParams.ach = paymentPlanData.ach
        }
        if (paymentPlanData.selected_card) {
          queryParams.selected_card = paymentPlanData.selected_card
        }
        if (paymentPlanData.billing_address) {
          queryParams.billing_address = paymentPlanData.billing_address
        }

        const filesLinks = []
        const filesNames = []
        const avatarLinks = []
        const avatarNames = []

        if (this.step.hasFileField) {
          const fileFields = this.formFields.filter(row => ['avatar', 'file_upload'].includes(row.type) && row.value)

          if (fileFields.length > 0) {
            fileFields.forEach(field => {
              if (Array.isArray(field.value)) {
                Array.from(field.value).forEach(val => {
                  if (val instanceof Blob) {
                    filesLinks.push(this.uploadFile(val))
                  } else {
                    filesNames.push(val)
                  }
                })
              } else {
                if (field.value instanceof Blob) {
                  avatarLinks.push(this.uploadFile(field.value, 'avatar'))
                } else {
                  avatarNames.push(field.value)
                }
              }
            })
          }
        }

        if (this.step.hasSignatureField) {
          const rows = this.$refs.rows || []
          const signatureFields = this.formFields.filter(field => field.type === 'signature')

          signatureFields.forEach(field => {
            const row = rows.find(r => r.fieldsData.ref === field.ref)

            if (row) {
              const sigData = row.getSignatureData()
              if (sigData) {
                field.signature = sigData
              }
            }
          })
        }

        const requests = []

        const fileRequests = filesLinks.map(link => ({ request: link, type: 'file' }))
        const avatarRequests = avatarLinks.map(link => ({ request: link, type: 'avatar' }))

        requests.push(...fileRequests, ...avatarRequests)


        Promise.all(requests.map(r => r.request))
          .then(responses => responses.forEach((response, index) => {
              const request = requests[index]

              if (!response.file_path) {
                store.commit('SET_LOADING_STATUS', false)
                this.isLoading = false
                this.errorMessage = 'Error uploading file'
                throw new Error('Error')
              }

              if (request.type === 'file') {
                filesNames.push(response.file_path)
              } else if (request.type === 'avatar') {
                avatarNames.push(response.file_path)
              }
            }))
          .then(() => {
            this.formFields
                .filter(row => !row.hidden)
                .forEach(row => {
                  if (row.type === 'file_upload' && row.value) {
                    let from = fileFieldsCount
                    let to = fileFieldsCount + row.value.length
                    let sliced = filesNames.slice(from, to)

                    queryParams.fields.push({
                      field_id: row.id,
                      files: sliced,
                    })
                    queryParams.form_data[row.name] = sliced

                    fileFieldsCount = to
                  } else if (row.type === 'avatar' && row.value) {
                    let avatarName = avatarNames[avatarFieldsCount]

                    queryParams.fields.push({
                      field_id: row.id,
                      files: [avatarName],
                    })
                    queryParams.form_data[row.name] = avatarName

                    avatarFieldsCount++
                  } else if(row.type === 'group_selection') {
                    let value
                    if (Array.isArray(row.value)) {
                      value = row.value.map(item => ({ id: item.id, name: item.name }))
                    } else {
                      value = { id: row.value?.id, name: row.value?.name }
                    }
                    queryParams.fields.push({
                      field_id: row.id,
                      result: value,
                    })
                  } else if (row.type === 'payment_field') {
                    queryParams.fields.push({
                      field_id: row.id,
                      result: {
                        payment_plan_activated: row.payment_plan_activated,
                        payment_month_selected: row.payment_month_selected,
                        charge_amount: row.charge_amount,
                      },
                    })
                  } else if (row.type === 'product_field') {
                    queryParams.fields.push({
                      field_id: row.id,
                      result: {
                        quantity: row.quantity,
                        amount: row.amount,
                        product_uuid: row.product_uuid,
                        product_name: row.product_name,
                      },
                    })
                  } else if (row.type === 'signature' && row.signature) {
                    queryParams.fields.push({
                      field_id: row.id,
                      result: row.signature,
                    })
                    queryParams.form_data[row.name] = row.signature
                  } else {
                    queryParams.fields.push({
                      field_id: row.id,
                      result: row.value,
                    })
                    queryParams.form_data[row.name] = row.value
                  }
                })
                
            if (paymentPlanData.paymentContractData &&
              (!this.paymentContractIsExist || (this.paymentContractIsExist && !this.stepStatus))
            ) {
              queryParams.paymentPlanData =  paymentPlanData.paymentContractData
            }

            store.dispatch('applicationRegistration/saveProgress', queryParams)
              .then(response => {
                if (response.status > 400) {

                  if (response.status === 409) {
                    // The application/step state changed (e.g. via an admin
                    // action) since this page was loaded. Don't show the raw
                    // API error inline — the parent will show a toast and
                    // refresh the whole application state so the user sees
                    // the latest version instead.
                    this.$emit('applicationStale')
                  } else if (response.status === 422) {
                    const validationErrors = Object.values(response.data.errors)
                    this.validationErrors = validationErrors.flat()
                  } else {
                    this.errorMessage = response.data.message
                  }

                  if (isDraft) {
                    this.isSaveButtonDisabled = false
                  } else {
                    this.isSendButtonDisabled = false
                  }

                  store.commit('SET_LOADING_STATUS', false)
                  this.isLoading = false
                  return
                } else {
                  store.commit('applicationRegistration/SET_APPLICATION_STATUS', response.data.application_status)
                  store.commit('applicationRegistration/SET_IS_APPLICATION_ACCEPTED', response.data.is_accepted)
                  store.commit('applicationRegistration/SET_HIDDEN_STATUS_FOR_STEPS_FROM_SETTINGS', { potentialStatus: response.data.potential_status })
                  store.commit('applicationRegistration/SET_EDITING_STATE_VERSION', response.data.editing_state_version)

                  this.step.status = response.data.step?.step_status
                  store.dispatch('applicationRegistration/recalculateStepEditable', this.step.id)
                  store.dispatch('alertMessage/fetchInfoMessages',{ application_id: response.data.application_id })

                  if (!this.applicationId) {
                    store.commit('applicationRegistration/SET_APPLICATION_ID', response.data.application_id)
                    this.$router.push({
                      name: 'application-create',
                      query: {user_id: this.userId, app_id: this.applicationId}
                    })
                  }

                  this.getAutoAssignedGroups(response.data.application_id)

                  if (!this.paymentContractIsExist && response.data.application_pay_condition) {
                    this.setPaymentContractData(response.data.application_pay_condition)
                  }

                  if (isDraft) {
                    this.isSaveButtonDisabled = false
                  } else {
                    this.isSendButtonDisabled = false
                  }

                  if (this.isLastStep) {
                    store.commit('SET_LOADING_STATUS', false)
                    this.isLoading = false
                    if (!isDraft) {
                      this.$toast({
                        component: ToastificationContent,
                        props: {
                          title: 'Successfully Completed',
                          icon: 'ThumbsUpIcon',
                          variant: 'info',
                        },
                      })
                      this.isSendButtonDisabled = true
                    }
                  } else {
                    store.commit('SET_LOADING_STATUS', false)
                    this.isLoading = false
                    this.$emit('nextStep')
                  }
                }
              })
              .catch((error) => {
                store.commit('SET_LOADING_STATUS', false)
                this.isLoading = false
                if (isDraft) {
                  this.isSaveButtonDisabled = false
                } else {
                  this.isSendButtonDisabled = false
                }
                this.errorMessage = error.message || 'An error occurred while saving progress'
              })
          })
          .catch((error) => {
            store.commit('SET_LOADING_STATUS', false)
            this.isLoading = false
            if (isDraft) {
              this.isSaveButtonDisabled = false
            } else {
              this.isSendButtonDisabled = false
            }
            this.errorMessage = error.message || 'An error occurred'
          })
      },
      async getAutoAssignedGroups(applicationId) {
        await store.dispatch('applicationRegistration/fetchAutoAssignedGroupsList', {
          application_id: applicationId,
        })
      },
      async setPaymentContractData(application_pay_condition) {
        store.commit('applicationRegistration/SET_APPLICATION_PAYMENT_OPTION', application_pay_condition)
        store.commit('applicationRegistration/DISABLE_EDITING_STEPS_IF_PAYMENT_CONTRACT_IS_SELECTED')
        store.commit('applicationRegistration/SET_EDITABLE_SETTINGS_FOR_STEPS')
      },
      registerAnotherChild() {
        this.$router.push({ name: 'application-select-child', params: { id: this.programId }})
      },
      registerForAnotherProgram() {
        this.$router.replace({ name: 'programs-list' })
      },
    }
  }
</script>

<style lang="scss">
@import 'src/assets/scss/colors/colors';

.step-info {
  display: block;
  position: relative;
  margin-left: 10px;
  background: $primary;
  text-align: center;
  color: #fff;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;

  &__tooltip {
    position: absolute;
    top: 30px;
    background: #F6E9EC;
    border-radius: 15px;
    width: 300px;
    padding: 13px 19px;
    color: $primary;
    top: -10px !important;
  }
}
.continue-btn {
  margin: 0;
  height: 44px;
}
</style>